import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CirclePlus, DollarSign, GitMerge, RefreshCw, Settings, ShieldCheck, SquarePen, Trash2, User } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import {
  approvalSettingsService,
  ApprovalRuleDTO,
  ApprovalRuleCategory,
} from '../../services/purchaseOrderService';
import './ApprovalSettingsPage.css';

const ApprovalSettingsPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();

  const [rules,          setRules]          = useState<ApprovalRuleDTO[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [refreshing,     setRefreshing]     = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string })?.message ?? null
  );

  const [deleteId,        setDeleteId]        = useState<number | null>(null);
  const [deleting,        setDeleting]        = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [activeCategory, setActiveCategory] = useState<'All' | ApprovalRuleCategory>('All');

  // ── Data ──────────────────────────────────────────────────────────────────

  const loadRules = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await approvalSettingsService.getApprovalRules();
      const data = res.data?.data;
      setRules(Array.isArray(data) ? data : []);
    } catch {
      setError(t('approvalRule.failedToLoad'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  useEffect(() => { loadRules(); }, [loadRules]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 4000);
    return () => clearTimeout(t);
  }, [successMessage]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const formatAmount = (min: number, max?: number | null) => {
    const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    return max == null ? `${fmt(min)} – ∞` : `${fmt(min)} – ${fmt(max)}`;
  };

  const filteredRules = (
    activeCategory === 'All' ? rules : rules.filter(r => r.ruleCategory === activeCategory)
  ).slice().sort((a, b) => a.level - b.level);

  const countFor = (cat: 'All' | ApprovalRuleCategory) =>
    cat === 'All' ? rules.length : rules.filter(r => r.ruleCategory === cat).length;

  const catLabel = (cat: ApprovalRuleCategory) =>
    cat === 'Purchase' ? t('approvalRule.categoryPurchase')
    : cat === 'GRN' ? t('approvalRule.categoryGRN')
    : t('approvalRule.categoryCancellation');

  const catClass = (cat: ApprovalRuleCategory) =>
    cat === 'Purchase' ? 'asr-cat-purchase'
    : cat === 'GRN' ? 'asr-cat-grn'
    : 'asr-cat-cancellation';

  const typeIcon = (type: string) =>
    type === 'Amount' ? <DollarSign size={13} />
    : type === 'Role' ? <User size={13} />
    : <GitMerge size={13} />;

  // ── Delete ────────────────────────────────────────────────────────────────

  const openDeleteModal  = (id: number) => { setDeleteId(id); setShowDeleteModal(true); };
  const closeDeleteModal = () => { setDeleteId(null); setShowDeleteModal(false); };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await approvalSettingsService.deleteApprovalRule(deleteId);
      setSuccessMessage(t('approvalRule.deletedSuccess'));
      closeDeleteModal();
      await loadRules();
    } catch {
      setError(t('approvalRule.failedToDelete'));
      closeDeleteModal();
    } finally {
      setDeleting(false);
    }
  };

  // ── Grid ──────────────────────────────────────────────────────────────────

  const columns: GridColumn[] = [
    {
      key: 'level',
      label: t('approvalRule.level'),
      width: '70px',
      sortable: true,
      render: (value) => <span className="asr-level-circle">{value}</span>,
    },
    {
      key: 'ruleCategory',
      label: t('approvalRule.category'),
      width: '130px',
      sortable: true,
      render: (value) => (
        <span className={`ims-badge ${catClass(value as ApprovalRuleCategory)}`}>
          {catLabel(value as ApprovalRuleCategory)}
        </span>
      ),
    },
    {
      key: 'ruleType',
      label: t('approvalRule.ruleType'),
      width: '130px',
      render: (value) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
          {typeIcon(value as string)} {value}
        </span>
      ),
    },
    {
      key: 'requiredRoleName',
      label: t('approvalRule.requiredRole'),
      width: '170px',
      render: (value) => value
        ? <span className="asr-role-pill"><User size={11} />{value}</span>
        : <span style={{ color: 'var(--ims-text-muted)' }}>—</span>,
    },
    {
      key: 'minAmount',
      label: t('approvalRule.amountRange'),
      hideTablet: true,
      render: (_value, row) => (
        <span className="asr-amount">{formatAmount(row.minAmount, row.maxAmount)}</span>
      ),
    },
    {
      key: 'description',
      label: t('common.description'),
      hideTablet: true,
      render: (value) => (
        <span style={{ color: 'var(--ims-text-sub)', fontSize: 13 }}>{value || '—'}</span>
      ),
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '90px',
      render: (value) => (
        <span className={`ims-badge ${value ? 'ims-badge-approved' : 'ims-badge-closed'}`}>
          {value ? t('common.active') : t('common.inactive')}
        </span>
      ),
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <SquarePen size={16} strokeWidth={1.75} />,
      onClick: (row) => navigate(`/settings/approval-rules/${(row as ApprovalRuleDTO).id}/edit`),
      className: 'grid-action-btn-edit',
      title: t('common.edit'),
    },
    {
      icon: <Trash2 size={16} strokeWidth={1.75} />,
      onClick: (row) => openDeleteModal((row as ApprovalRuleDTO).id),
      className: 'grid-action-btn-delete',
      title: t('common.delete'),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="approval-settings-page">

        {/* Header */}
        <div className="ims-list-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--ims-accent-bg)', color: 'var(--ims-accent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="ims-list-title">{t('approvalRule.title')}</h1>
              <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, margin: 0 }}>
                {t('approvalRule.subtitle')}
              </p>
            </div>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={() => loadRules(true)} disabled={refreshing}>
              <RefreshCw size={14} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
            <button className="btn-new" onClick={() => navigate('/settings/approval-rules/create')}>
              <CirclePlus size={16} />
              {t('approvalRule.addRule')}
            </button>
          </div>
        </div>

        {/* Stat chips */}
        {!loading && (
          <div className="ims-stats-row" style={{ marginBottom: 16 }}>
            <div className="ims-stat-card">
              <div className="ims-stat-icon asr-icon-total"><Settings size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{rules.length}</span>
                <span className="ims-stat-label">{t('approvalRule.totalRules')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon asr-icon-purchase"><ShieldCheck size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{countFor('Purchase')}</span>
                <span className="ims-stat-label">{t('approvalRule.categoryPurchase')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon asr-icon-grn"><ShieldCheck size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{countFor('GRN')}</span>
                <span className="ims-stat-label">{t('approvalRule.categoryGRN')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-icon asr-icon-cancel"><ShieldCheck size={18} /></div>
              <div className="ims-stat-info">
                <span className="ims-stat-value">{countFor('Cancellation')}</span>
                <span className="ims-stat-label">{t('approvalRule.categoryCancellation')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 12 }}>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}
        {successMessage && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 12 }}>
            <span>{successMessage}</span>
            <button type="button" onClick={() => setSuccessMessage(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* How it works */}
        <div className="ims-alert ims-alert-info" style={{ marginBottom: 16, fontSize: 13 }}>
          <ShieldCheck size={15} style={{ flexShrink: 0 }} />
          <span><strong>{t('approvalRule.howItWorksTitle')}:</strong> {t('approvalRule.howItWorksText')}</span>
        </div>

        {/* Category tabs */}
        <div className="asr-tabs">
          {(['All', 'Purchase', 'GRN', 'Cancellation'] as const).map(cat => (
            <button
              key={cat}
              className={`asr-tab${activeCategory === cat ? ' asr-tab--active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === 'All' ? t('approvalRule.categoryAll') : catLabel(cat as ApprovalRuleCategory)}
              <span className="asr-tab-count">{countFor(cat)}</span>
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="ims-grid-wrapper">
          <DataGrid
            data={filteredRules}
            columns={columns}
            actions={actions}
            loading={loading}
            pageSize={10}
            searchPlaceholder={t('common.search')}
            emptyMessage={t('approvalRule.noRulesFound')}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </div>

      </div>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="ims-modal-overlay">
          <div className="ims-modal" style={{ maxWidth: 420 }}>
            <div style={{ padding: '24px 24px 0' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10,
                background: 'var(--ims-red-bg)', color: 'var(--ims-red-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
              }}>
                <Trash2 size={20} />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: 'var(--ims-text-main)' }}>
                {t('approvalRule.deleteTitle')}
              </h3>
              <p style={{ margin: 0, fontSize: 14, color: 'var(--ims-text-sub)', lineHeight: 1.5 }}>
                {t('approvalRule.deleteConfirm')}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '20px 24px' }}>
              <button className="btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                {t('common.cancel')}
              </button>
              <button className="btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting && <span className="ims-btn-spinner" />}
                {deleting ? t('common.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default ApprovalSettingsPage;
